/**
 * MoveGoodsSeattlePhase - Seattle Metro Solo 商品輸送フェーズUIコンポーネント
 *
 * 共通の MoveGoodsPhase をラップし、Seattle 固有の UI を追加:
 * - 輸送回数表示（cargoTransportLevel ベース）
 * - Gray City 選択モーダル（deliver/keep）
 * - Airport 留置状況表示
 *
 * @see docs/specifications/logic/seattle_metro_solo/phase_05_move_goods_seattle.md
 */

import { useState } from 'react';
import type { SeattleGameState } from '../../types';
import type { TransportableGoods, BestRoute } from '../../types/ui-interfaces';
import { MoveGoodsPhase } from './MoveGoodsPhase';
import { useTranslations } from '../../i18n';

/** Gray City のID */
const GRAY_CITIES = ['DOWNTOWN', 'AIRPORT'] as const;

export interface MoveGoodsSeattlePhaseProps {
  G: SeattleGameState;
  isMyTurn: boolean;
  transportableGoods: TransportableGoods[];
  selectedGoods: TransportableGoods | null;
  bestRoute: BestRoute | null;
  alternativeRoutes: BestRoute[];
  showUpgradeConfirm: boolean;
  onSelectGoods: (goods: TransportableGoods) => void;
  onSelectRoute: (route: BestRoute) => void;
  onConfirmTransport: (grayCityAction?: 'deliver' | 'keep') => void;
  onUpgradeLoco: () => void;
  onShowUpgradeConfirm: () => void;
  onHideUpgradeConfirm: () => void;
  onPass: () => void;
}

/**
 * Phase 5: 商品輸送フェーズUI（Seattle専用ラッパー）
 *
 * 共通の MoveGoodsPhase を使用し、Seattle 固有の UI を追加
 */
export function MoveGoodsSeattlePhase({
  G,
  isMyTurn,
  transportableGoods,
  selectedGoods,
  bestRoute,
  alternativeRoutes,
  showUpgradeConfirm,
  onSelectGoods,
  onSelectRoute,
  onConfirmTransport,
  onUpgradeLoco,
  onShowUpgradeConfirm,
  onHideUpgradeConfirm,
  onPass,
}: MoveGoodsSeattlePhaseProps) {
  const t = useTranslations();
  const player = G.players['0'];

  // 特別ルール折りたたみの状態
  const [showSpecialRules, setShowSpecialRules] = useState(false);

  // Gray City 選択モーダルの状態
  const [showGrayCityModal, setShowGrayCityModal] = useState(false);
  const [pendingGrayCityAction, setPendingGrayCityAction] = useState<{
    fromCityId: string;
    toCityId: string;
    goodsColor: string;
    path: string[];
  } | null>(null);

  // 輸送回数（共通カウンタを使用）
  const maxDeliveries = G.maxDeliveriesThisPhase ?? G.cargoTransportLevel;
  const remainingDeliveries = maxDeliveries - player.turnsCompletedThisPhase;

  // Airport の留置商品を取得（容量チェック用）
  const airportHex = G.map.find(h => h.city?.id === 'AIRPORT');
  const goodsAtAirport = airportHex?.goods || [];

  // Airport 留置が上限に達しているか
  const isAirportFull = goodsAtAirport.length >= 3;

  // 目的地が Gray City かどうか
  const isDestinationGrayCity = bestRoute && GRAY_CITIES.includes(bestRoute.destinationId as typeof GRAY_CITIES[number]);
  const isDestinationAirport = bestRoute?.destinationId === 'AIRPORT';

  // 輸送確定ハンドラ（Gray City の場合はモーダルを表示）
  const handleConfirmTransport = () => {
    if (isDestinationGrayCity && selectedGoods && bestRoute) {
      setPendingGrayCityAction({
        fromCityId: selectedGoods.cityId,
        toCityId: bestRoute.destinationId,
        goodsColor: selectedGoods.color,
        path: bestRoute.path,
      });
      setShowGrayCityModal(true);
    } else {
      onConfirmTransport();
    }
  };

  // Gray City 選択確定
  const handleGrayCityAction = (action: 'deliver' | 'keep') => {
    setShowGrayCityModal(false);
    setPendingGrayCityAction(null);
    onConfirmTransport(action);
  };

  // 共通コンポーネント用の player オブジェクト
  // Issue #9 + Technical Debt #1: 共通カウンタを使用（オーバーライド不要）
  const playerForCommon = player;

  return (
    <div className="flex flex-col h-full">
      {/* Seattle 固有: 輸送回数表示 */}
      {isMyTurn && (
        <div className="flex-shrink-0 pb-2 border-b mb-2">
          <div className="flex justify-between items-center">
            <span className="text-xs text-muted-foreground">
              {t.moveGoods?.seattle?.deliveriesRemaining || 'Deliveries remaining'}:
            </span>
            <span className="text-lg font-bold">
              {remainingDeliveries}/{maxDeliveries}
            </span>
          </div>
        </div>
      )}

      {/* Seattle 固有: 特別ルール折りたたみ */}
      {isMyTurn && (
        <div className="flex-shrink-0 mb-2">
          <button
            className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => setShowSpecialRules(!showSpecialRules)}
          >
            <span className="text-[10px]">{showSpecialRules ? '▲' : '▼'}</span>
            <span>{t.moveGoods?.seattle?.specialRulesTitle || 'Special Rules'}</span>
          </button>
          {showSpecialRules && (
            <div className="mt-1 p-2 bg-muted/50 rounded text-[10px] text-muted-foreground space-y-1">
              <p>{t.moveGoods?.seattle?.specialRule1}</p>
              <p>{t.moveGoods?.seattle?.specialRule2}</p>
            </div>
          )}
        </div>
      )}

      {/* 共通コンポーネント */}
      <div className="flex-1 min-h-0">
        <MoveGoodsPhase
          player={playerForCommon}
          transportableGoods={transportableGoods}
          selectedGoods={selectedGoods}
          bestRoute={bestRoute}
          alternativeRoutes={alternativeRoutes}
          isMyTurn={isMyTurn}
          showUpgradeConfirm={showUpgradeConfirm}
          errorMessage=""
          onSelectGoods={onSelectGoods}
          onSelectRoute={onSelectRoute}
          onConfirmTransport={handleConfirmTransport}
          onUpgradeLoco={onUpgradeLoco}
          onShowUpgradeConfirm={onShowUpgradeConfirm}
          onHideUpgradeConfirm={onHideUpgradeConfirm}
          onPass={onPass}
        />
      </div>

      {/* Seattle 固有: Gray City 選択モーダル */}
      {showGrayCityModal && pendingGrayCityAction && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background border rounded-lg p-4 max-w-sm w-full mx-4 space-y-4">
            <h3 className="font-heading font-semibold text-lg">
              {t.moveGoods?.seattle?.grayCityTitle || 'Deliver to Gray City'}
            </h3>
            <p className="text-sm text-muted-foreground">
              {t.moveGoods?.seattle?.grayCityPrompt || 'Choose what to do with the goods:'}
            </p>

            <div className="space-y-2">
              {/* 取り除く */}
              <button
                className="w-full action-card text-left"
                onClick={() => handleGrayCityAction('deliver')}
              >
                <div className="font-heading text-sm font-semibold">
                  {t.moveGoods?.seattle?.deliver || 'Deliver (Remove)'}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {t.moveGoods?.seattle?.deliverDesc || 'Remove from game, +1VP at end'}
                </div>
              </button>

              {/* 留置する */}
              <button
                className="w-full action-card text-left"
                onClick={() => handleGrayCityAction('keep')}
                disabled={isDestinationAirport && isAirportFull}
              >
                <div className="font-heading text-sm font-semibold">
                  {t.moveGoods?.seattle?.keep || 'Keep (Store)'}
                </div>
                <div className="text-[10px] text-muted-foreground">
                  {isDestinationAirport
                    ? `${t.moveGoods?.seattle?.keepDescAirport || 'Store at Airport'} (${goodsAtAirport.length}/3)`
                    : t.moveGoods?.seattle?.keepDescDowntown || 'Store at Downtown (unlimited)'}
                </div>
                {isDestinationAirport && isAirportFull && (
                  <div className="text-xs text-destructive mt-1">
                    {t.moveGoods?.seattle?.airportFull || 'Airport is full'}
                  </div>
                )}
              </button>
            </div>

            <button
              className="btn-secondary w-full py-2"
              onClick={() => {
                setShowGrayCityModal(false);
                setPendingGrayCityAction(null);
              }}
            >
              {t.common.cancel}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default MoveGoodsSeattlePhase;
